'use client';

import styled from '@emotion/styled';
import { useMemo } from 'react';
import { WebViewScreen } from '@diritto/common/app/webview-header.constant';
import { isPC, PCOffset } from '@/constants/layout';
import { useResponsiveImageMap } from '@/ui-hooks/useResponsiveImageMap';
import { useViewPort } from '@/ui-hooks/useViewPort';
import { configure } from '@/utils/configure';
import { MutableHeaderLayout } from 'ui-components/layout/MutableHeaderLayout';
import { HeaderNavigationBarProps } from 'ui-components/navigations/HeaderNavigationBar';
import { useInAppHeader } from 'ui-hooks/useInAppHeader';
import { useInAppStatusBar } from 'ui-hooks/useInAppStatusBar';

export const PublisherOpenPage = ({
  isApp = false,
  headerProps = {},
}: {
  isApp?: boolean;
  headerProps?: HeaderNavigationBarProps;
}) => {
  useInAppHeader({ screen: WebViewScreen.Event, title: '디리토 이제 출간도 해요!' });
  useInAppStatusBar(WebViewScreen.Event);

  const { width: screenWidth } = useViewPort();

  const scale = useMemo(() => (screenWidth > PCOffset ? Math.min(screenWidth, 730) / 730 : 1), [screenWidth]);

  useResponsiveImageMap('publisher-open-content-1', 'publisher-open-content-1-map');
  useResponsiveImageMap('publisher-open-faq', 'publisher-open-faq-map');

  return (
    <MutableHeaderLayout hasTab={false} isApp={isApp} title="디리토 이제 출간도 해요!" noBottomPadding {...headerProps}>
      <div className="flex min-h-screen bg-[#BF9CFF]">
        <div className="flex flex-col justify-center w-full bg-[#BF9CFF]">
          <img
            src={`${configure.staticPrefix}/images/event/publisher-open/publisher-open-top.png`}
            alt="디리토 출간 안내 상단 이미지"
            className="w-full max-w-full h-auto object-contain"
          />
          <div className="flex justify-center w-full bg-[#BF9CFF]">
            <img
              src={`${configure.staticPrefix}/images/event/publisher-open/publisher-open-content-1.png`}
              alt="디리토 출간 안내 내용 이미지"
              id="publisher-open-content-1"
              useMap="#publisher-open-content-1-map"
              className="w-full max-w-full h-auto object-contain"
            />
            <StyledScaleWrapper scale={scale} className="absolute left-0 w-full">
              <map name="publisher-open-content-1-map">
                <area
                  target="_self"
                  alt="출판사 계약 의지 밝히기 안내 이동"
                  title="출판사 계약 의지 밝히기 안내"
                  href="https://help.diritto.co.kr/hc/ko/articles/9530982418831"
                  coords="656,3340,134,3285"
                  shape="rect"
                />
              </map>
            </StyledScaleWrapper>
          </div>
          <div className="flex justify-center w-full bg-white">
            <img
              src={`${configure.staticPrefix}/images/event/publisher-open/publisher-open-faq.png`}
              alt="디리토 출간 안내 FAQ 이미지"
              id="publisher-open-faq"
              useMap="#publisher-open-faq-map"
              className="w-full max-w-full h-auto object-contain"
            />
            <StyledScaleWrapper scale={scale} className="absolute left-0 w-full">
              <map name="publisher-open-faq-map">
                <area
                  target="_self"
                  alt="메시지 기능 안내"
                  title="메시지 기능 안내"
                  href="https://www.diritto.co.kr/contents/2381902/episodes/3918414"
                  coords="529,977,678,1062"
                  shape="rect"
                />
              </map>
            </StyledScaleWrapper>
          </div>
        </div>
      </div>
    </MutableHeaderLayout>
  );
};

export const StyledScaleWrapper = styled.div<{ scale: number }>`
  transform: scale(${({ scale = 0.3 }) => scale});
  transition: width 0.3s ease-in-out;

  @media (${isPC}) {
    transform: scale(1);
  }
`;
